// WarehouseAssortmentNewRuleUpdateComponent.js
import React from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormHelperText,
  Dialog,
  List,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Sidebar from "../../../Navigation/Sidebar/Sidebar";
import "./WarehouseRuleCreation.css";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import LoadingSpinner from "../../../Loading/component";
import ErrorIcon from '@mui/icons-material/Error';

const WarehouseRuleUpdateComponent = ({
  formData,
  searchInput,
  searchResults,
  searchLoading,
  showDropdown,
  errors,
  isSubmitting,
  loading,
  ruleTypes,
  onChange,
  onSearchInputChange,
  onAddName,
  onRemoveName,
  onSlotCountChange,
  onSubmit,
  onDateChange,
  showSuccessModal,
  newRuleId,
  setShowSuccessModal,
  fetchRuleWarehouse,
  fetchRuleId,
  ruleFailures,
  ruleFailureData,
  setShowFailureModal,
  showFailureModal,
  isMvidSearch = false
}) => {
  const navigate = useNavigate();

  const isSaveDisabled = () => {
    if (!formData.ruleType) return true;
    if (formData.ruleType === "OfferPrice") {
      return !formData.maxPrice;
    }
    return formData.names.length === 0 || !formData.validationType;
  };



  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="warehouse-rule-container">
          <Sidebar />

          <div className="warehouse-rule-content">
            <div className="warehouse-rule-header">
              <button onClick={() => navigate(-1)} className="warehouse-rule-back-button">←</button>
              <h2 className="warehouse-rule-title">Edit Rule</h2>
            </div>

            <div className="warehouse-rule-form">
              {/* Start Date */}
              <div className="warehouse-rule-section">
                <Typography className="warehouse-rule-section-title">Select Start Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.startDate}
                    onChange={onDateChange}
                    minDate={new Date()} // 👈 Prevents selecting past dates
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        variant="outlined"
                        size="small"
                        className="warehouse-rule-input"
                        disabled={isSubmitting}
                      />
                    )}
                    disabled={isSubmitting}
                  />
                </LocalizationProvider>
              </div>


              <div className="warehouse-rule-divider"></div>

              {/* Rule Type */}
              <div className="warehouse-rule-section">
                <Typography className="warehouse-rule-section-title">Select Rule Type</Typography>
                <RadioGroup
                  name="ruleType"
                  value={formData.ruleType}
                  onChange={onChange}
                  className="warehouse-rule-radio-group"
                >
                  <div className="warehouse-rule-radio-grid">
                    {ruleTypes.map((type) => (
                      <FormControlLabel
                        key={type.value}
                        value={type.value}
                        control={<Radio className="warehouse-rule-radio" disabled={isSubmitting} />}
                        label={
                          <div className="rule-type-option">
                            <span className="rule-type-icon-container">
                              <img src={type.icon} className="rule-type-icon" alt={type.label} />
                            </span>
                            <span className="rule-type-label">{type.label}</span>
                          </div>
                        }
                        className="warehouse-rule-radio-label"
                        disabled={isSubmitting}
                      />
                    ))}
                  </div>
                </RadioGroup>
                {errors.ruleType && (
                  <FormHelperText error className="error-message">
                    Please select a rule type
                  </FormHelperText>
                )}
              </div>

              <div className="warehouse-rule-divider"></div>

              {/* Name or Max Price */}
              <div className="warehouse-rule-section">
                <Typography className="warehouse-rule-section-title">
                  {formData.ruleType === "OfferPrice" ? "Max Price" : "Name"}
                </Typography>
                {formData.ruleType === "OfferPrice" ? (
                  <>
                    <TextField
                      type="text"
                      placeholder="Enter max price"
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="maxPrice"
                      value={formData.maxPrice || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const regex = /^\d{0,4}(\.\d{0,2})?$/; // Allows 0-4 digits before decimal, up to 2 digits after decimal
                        if (value === "" || regex.test(value)) {
                          onChange(e);
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        disabled: isSubmitting
                      }}
                      className="warehouse-rule-input"
                      error={errors.maxPrice}
                      disabled={isSubmitting}
                    />
                    {errors.maxPrice && (
                      <FormHelperText error className="error-message">
                        Please enter a max price
                      </FormHelperText>
                    )}
                  </>

                ) : (
                  <>
                    <div className="warehouse-rule-search-container">
                      <TextField
                        value={searchInput}
                        onChange={onSearchInputChange}
                        placeholder={`Search ${formData.ruleType === "MVID" ? "Manufacturer Variant ID" : formData.ruleType + " Name"}...`}
                        fullWidth
                        variant="outlined"
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: searchLoading && (
                            <InputAdornment position="end">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ),
                          disabled: isSubmitting || !formData.ruleType
                        }}
                        className="warehouse-rule-input warehouse-rule-search-input"
                        disabled={isSubmitting || !formData.ruleType}
                      />
                      {showDropdown && (
                        <div className="warehouse-rule-dropdown">
                          {searchLoading ? (
                            <div className="warehouse-rule-dropdown-item">
                              <CircularProgress size={20} />
                            </div>
                          ) : searchResults.length > 0 ? (
                            searchResults.map(item => (
                              <div
                                key={item.id}
                                onClick={() => onAddName(item)}
                                className="warehouse-rule-dropdown-item"
                              >
                                {isMvidSearch ? (
                                  <div>
                                    <div><strong>{item.id}</strong> </div>
                                    {item.displayName && (
                                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                        {item.displayName.split(' - ')[1]}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <span>{item.id} <span style={{ color: "#888" }}>({item.name})</span></span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="warehouse-rule-dropdown-no-results">
                              {searchInput ? "No results found" : "Start typing to search"}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="warehouse-rule-selected-names">
                      {formData.names.map(item => (
                        <Chip
                          key={item.id}
                          label={formData.ruleType === "MVID" ? `${item.name} ` : item.name}
                          onDelete={isSubmitting ? undefined : () => onRemoveName(item.id)}
                          deleteIcon={<CloseIcon />}
                          className="warehouse-rule-chip"
                          disabled={isSubmitting}
                        />
                      ))}
                    </div>
                    {errors.names && (
                      <FormHelperText error className="error-message">
                        Please select at least one {formData.ruleType.toLowerCase()}
                      </FormHelperText>
                    )}
                  </>
                )}
              </div>

              {formData.ruleType !== "OfferPrice" && (
                <>
                  <div className="warehouse-rule-divider"></div>
                  <div className="warehouse-rule-section">
                    <Typography className="warehouse-rule-section-title">Select Validation Type</Typography>
                    <RadioGroup
                      name="validationType"
                      value={formData.validationType}
                      onChange={onChange}
                      className="warehouse-rule-validation-group"
                    >
                      <div className="warehouse-rule-slot-container">
                        <FormControlLabel
                          value="QUANTITY"
                          control={<Radio className="warehouse-rule-radio" disabled={isSubmitting} />}
                          label="MANDATORY"
                          className="warehouse-rule-radio-label"
                          disabled={isSubmitting}
                        />

                        {/* slot control wrapper */}
                        <div
                          className={`warehouse-rule-slot-controls ${formData.validationType === "RESTRICTED" ? "disabled" : ""
                            }`}
                        >
                          <IconButton
                            onClick={() => !isSubmitting && onSlotCountChange(-1)}
                            disabled={formData.slotCount <= 1 || isSubmitting || formData.validationType === "RESTRICTED"}
                            className="warehouse-rule-slot-button"
                          >
                            <RemoveIcon />
                          </IconButton>

                          <Typography className="warehouse-rule-slot-value">
                            {formData.slotCount}
                          </Typography>

                          <IconButton
                            onClick={() => !isSubmitting && onSlotCountChange(1)}
                            disabled={isSubmitting || formData.validationType === "RESTRICTED"}
                            className="warehouse-rule-slot-button"
                          >
                            <AddIcon />
                          </IconButton>
                        </div>
                      </div>

                      <Typography className="warehouse-rule-hint-text">
                        In case of adding multiple selections in MANDATORY, Rule will check validation for ANY 1 selection
                      </Typography>

                      <div className="warehouse-rule-slot-container">
                        <FormControlLabel
                          value="RESTRICTED"
                          control={<Radio className="warehouse-rule-radio" disabled={isSubmitting} />}
                          label="Restricted"
                          className="warehouse-rule-radio-label"
                          disabled={isSubmitting}
                        />
                      </div>

                      <Typography className="warehouse-rule-hint-text">
                        In case of adding multiple selections in RESTRICTED, Rule will check validation For ALL selections
                      </Typography>
                    </RadioGroup>

                    {errors.validationType && (
                      <FormHelperText error className="error-message">
                        Please select a validation type
                      </FormHelperText>
                    )}
                  </div>

                </>
              )}
              <Dialog
                open={showSuccessModal}
                onClose={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                PaperProps={{
                  style: { borderRadius: 20, padding: '20px 30px', textAlign: 'center' }
                }}
              >
                <CheckCircleIcon style={{ fontSize: 60, color: "#00b300", margin: '10px auto' }} />

                <Typography variant="h6" gutterBottom>
                  Successfully Updated
                </Typography>

                <Typography variant="body1">
                  Rule Updated Successfully (ID <strong>{fetchRuleId}</strong>)
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  For <strong>{fetchRuleWarehouse}</strong>
                </Typography>

                <Button
                  variant="contained"
                  sx={{ mt: 3, backgroundColor: '#00b300', '&:hover': { backgroundColor: '#009900' } }}
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(-1);
                  }}
                >
                  Ok
                </Button>
              </Dialog>
              <Dialog
                open={showFailureModal}
                onClose={() => setShowFailureModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                  style: { borderRadius: 20, padding: '20px' }
                }}
              >
                <Box display="flex" flexDirection="column" alignItems="center" p={2}>
                  <ErrorIcon style={{ fontSize: 60, color: "#f44336", marginBottom: 16 }} />
                  <Typography variant="h6" gutterBottom color="error">
                    Rule Creation Failed
                  </Typography>

                  {ruleFailureData && ruleFailureData.length > 0 && (
                    <Box width="100%" mt={2}>

                      <List dense>
                        {ruleFailures.map((failure, index) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemText
                                primary={
                                  <Typography variant="body1" color="error">
                                    {failure.failureType.replace(/_/g, ' ')}
                                  </Typography>
                                }
                                secondary={
                                  failure.failedItems.ids.length > 0 ? (
                                    <Typography variant="body2">
                                      IDs: {failure.failedItems.ids.join(', ')}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2">
                                      {failure.failedItems.message}
                                    </Typography>
                                  )
                                }
                              />
                            </ListItem>
                            {index < ruleFailures.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    color="error"
                    sx={{ mt: 3 }}
                    onClick={() => setShowFailureModal(false)}
                  >
                    Close
                  </Button>
                </Box>
              </Dialog>
              {/* Action Buttons */}
              <div className="warehouse-rule-actions">
                <Button
                  variant="outlined"
                  onClick={() => navigate(-1)}
                  className="warehouse-rule-cancel-button"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={onSubmit}
                  className="warehouse-rule-save-button"
                  disabled={isSaveDisabled() || isSubmitting}
                  sx={{
                    '&.Mui-disabled': {
                      backgroundColor: '#b0b0b0',
                      color: '#fff'
                    }
                  }}
                >
                  Update Rule
                </Button>

              </div>
            </div>
          </div>
        </div>
      )}
    </>

  );
};

export default WarehouseRuleUpdateComponent;